import { useMemo, useState, type CSSProperties } from "react";
import { useReducedMotion } from "framer-motion";
import { useTranslation } from "@/i18n/I18nProvider";
import { cn } from "@/lib/cn";
import type { TickerEvent } from "@/types/events";
import { TickerItem } from "./TickerItem";

export interface StockTickerProps {
  events: TickerEvent[];
  /** Pixels per second. Higher = faster scroll. Default: 28 (slow + readable). */
  speed?: number;
  /** Pause the marquee when the user hovers over it. Default: true. */
  pauseOnHover?: boolean;
  /** Optional override className for the outer wrapper. */
  className?: string;
  /** Visually-hidden label announced to screen readers. */
  ariaLabel?: string;
  /** Keep scrolling under reduced motion (the `/showcase-cluj` demo only). */
  forceMotion?: boolean;
}

const FALLBACK_TRACK_WIDTH = 1200;

/**
 * Horizontal marquee.
 *
 * Animation is a CSS keyframe (`resto-marquee` in globals.css) that moves a
 * shrink-wrapped pair of identical tracks by -50% of their combined width —
 * seamless looping with no measured distance to drift out of date.
 * Hovering toggles `animation-play-state: paused`, which freezes
 * the animation at its current frame *without* resetting `transform` — so
 * the ticker stops in place and resumes from the same spot.
 */
export function StockTicker({
  events,
  speed = 28,
  pauseOnHover = true,
  className,
  ariaLabel,
  forceMotion = false,
}: StockTickerProps) {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const [trackWidth, setTrackWidth] = useState<number>(FALLBACK_TRACK_WIDTH);
  const [isPaused, setIsPaused] = useState(false);

  const measureRef = (node: HTMLDivElement | null) => {
    if (node && node.offsetWidth > 0 && node.offsetWidth !== trackWidth) {
      setTrackWidth(node.offsetWidth);
    }
  };

  const duration = useMemo(() => {
    if (!trackWidth || speed <= 0) return 30;
    return trackWidth / speed;
  }, [trackWidth, speed]);

  if (events.length === 0) return null;

  // The keyframe travels -50% of the w-max wrapper (= exactly one track), so
  // the loop is seamless by construction; trackWidth only tunes the speed.
  const animate = forceMotion || !prefersReducedMotion;
  const animationStyle: CSSProperties = animate
    ? ({
        animation: `resto-marquee ${duration}s linear infinite`,
        animationPlayState: isPaused ? "paused" : "running",
        willChange: "transform",
        // Read by `.ticker-force-motion`, which out-ranks the global
        // reduced-motion clamp (globals.css).
        ...(forceMotion ? { "--ticker-duration": `${duration}s` } : {}),
      } as CSSProperties)
    : {};

  return (
    <div
      role="region"
      aria-label={ariaLabel ?? t("TICKER_LABEL")}
      className={cn(
        "group relative w-full border-y border-white/10 light:border-black/10 bg-zinc-950 light:bg-white",
        // Reduced motion: don't auto-scroll. Fall back to a static strip the
        // user can swipe through, instead of a frozen, clipped, broken-looking
        // row. Without this, "disable animations" makes the ticker look dead.
        animate ? "overflow-hidden" : "overflow-x-auto",
        className,
      )}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      onFocusCapture={() => pauseOnHover && setIsPaused(true)}
      onBlurCapture={() => pauseOnHover && setIsPaused(false)}
    >
      {/* Edge fades — only while animating; on the static strip they'd hide
          the first/last items the user is trying to read. */}
      {animate && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-zinc-950 to-transparent sm:w-16"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-zinc-950 to-transparent sm:w-16"
          />
        </>
      )}

      <div className={cn("flex w-max py-3", forceMotion && "ticker-force-motion")} style={animationStyle}>
        {/* Primary track (measured) */}
        <div ref={measureRef} className="flex shrink-0 items-center">
          {events.map((event) => (
            <TickerItem key={`a-${event.id}`} event={event} />
          ))}
        </div>
        {/* Duplicate track — only needed for the seamless loop while animating. */}
        {animate && (
          <div aria-hidden className="flex shrink-0 items-center">
            {events.map((event) => (
              <TickerItem key={`b-${event.id}`} event={event} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
